import {createStyles} from '@mantine/core';
import React, {useEffect, useState} from 'react';

import {getArchiveText} from 'archive/ArchivePurposesUtil';
import isArchived from 'archive/isArchived';
import {exportAuditLogs} from 'audit/AuditActions';
import {pathToURL} from 'BenchlingUtil';
import {ActionOrigin} from 'constants/AnalyticsConstants';
import {MAX_PROCEDURE_RUN_DESCRIPTION_LENGTH} from 'procedures/design/ProcedureDesignConstants';
import {getProcedureRunIcon} from 'procedures/design/procedureIconUtils';
import * as ProcedureRunDAO from 'procedures/network/ProcedureRunDAO';
import {ProcedureRunTab} from 'studies/redux/ProcedureRunPlanUIReduxActions';
import {StudyOverviewTabContentsProps} from 'studies/typed/utils/TypedStudyTypes';
import {useAnalyzeProcedureRunAction} from 'studies/typed/utils/ui/useAnalyzeProcedureRunAction';
import {useLinkToExistingUnitOperationsAction} from 'studies/typed/utils/ui/useLinkToExistingUnitOperationsAction';
import {getProcedureRunUIStringForStudyType, getProcedureTypeForStudyType} from 'studies/TypedStudyUtils';
import Header, {type ActionItem, type BadgeInfo, type FullTextButton} from 'ui-library/headers/Header';
import EditableTextField, {TextFieldType} from 'ui-library/inputs/EditableTextField';
import copyToClipboard from 'util/clipboard/copyToClipboard';

import {StudyDesignTabEditState} from '../design-tabs/StudyDesignTabSavingButtons';
import {
  actionToFullTextButton,
  CreatedAt,
  getProcedureRunHeaderButtons,
  type StudyHeaderFields,
} from './procedureRunHeaderUtils';
import {useProcedureRunOverviewScroll} from './procedureRunOverviewScroll';
import {useProcedureRunContext, useStudyOverviewContext} from './StudyOverviewContexts';

type StudyNode = StudyOverviewTabContentsProps['study'];

interface ProcedureRunHeaderStudy extends StudyHeaderFields {
  humanId: string;
  archiveRecord: StudyNode['archiveRecord'];
}

export interface ProcedureRunHeaderProps {
  study: ProcedureRunHeaderStudy;
  procedureRunId: string;
  procedureRunName: string;
  /** Plan promoted into this run, if any. */
  procedureRunPlanId?: string;
  procedureRunDescription?: string;
  procedureRunCreatedAt?: string;
  isProcedureRunArchived: boolean;
  refetch: () => void;
  onStudyCrumbClick: () => void;
}

export default function ProcedureRunHeader({
  study,
  procedureRunId,
  procedureRunName,
  procedureRunPlanId,
  procedureRunDescription,
  procedureRunCreatedAt,
  isProcedureRunArchived,
  refetch,
  onStudyCrumbClick,
}: ProcedureRunHeaderProps): JSX.Element {
  const {classes} = useStyles();
  const scroll = useProcedureRunOverviewScroll();
  const studyType = study.studySchema.studyType;
  const runLabel = getProcedureRunUIStringForStudyType(studyType);
  const {runDesignActions} = useProcedureRunContext();
  const {currentProcedureRunTab, setIsProcedureRunDescriptionInEditMode} = useStudyOverviewContext();
  const isDesignTab = currentProcedureRunTab === ProcedureRunTab.DESIGN;
  const isEditingDesign = isDesignTab && runDesignActions?.editState === StudyDesignTabEditState.EDITING;
  const shouldCollapseHeader = (scroll?.isHeaderCollapsed ?? false) || isEditingDesign;

  const linkToExistingUnitOperationsAction = useLinkToExistingUnitOperationsAction({
    studyId: study.id,
    procedureRunId,
    procedureRunName,
    studyType,
  });
  const analyzeAction = useAnalyzeProcedureRunAction({
    study: {id: study.id, name: study.name, humanId: study.humanId},
    isStudyArchived: study.archiveRecord != null,
    studyType,
    procedureRunId,
    procedureRunName,
    isProcedureRunArchived,
    procedureType: getProcedureTypeForStudyType(studyType),
    actionOrigin: ActionOrigin.STUDY_RUN_DETAILS_PAGE,
  });

  const auditLogIds = [procedureRunId, procedureRunPlanId].filter((id): id is string => id != null);
  const copyLinkAction: ActionItem = {
    icon: 'link',
    name: `Copy ${runLabel} link`,
    onSelect: () =>
      copyToClipboard(pathToURL(`${study.editUrl}?runId=${procedureRunId}`), {
        showToastOnSuccess: true,
        toastText: `Copied ${runLabel} link.`,
      }),
  };
  const exportAuditAction: ActionItem = {
    icon: 'audit-trail',
    name: 'Export audit log',
    onSelect: () => {
      exportAuditLogs(auditLogIds);
    },
  };

  const [runDescription, setRunDescription] = useState(procedureRunDescription ?? '');
  useEffect(() => {
    setRunDescription(procedureRunDescription ?? '');
  }, [procedureRunDescription, procedureRunId]);

  const descriptionEditor = (
    <div className={classes.descriptionEditor}>
      <EditableTextField
        requestChange={async (updated: string | number) => {
          const description = updated as string;
          setRunDescription(description);
          await ProcedureRunDAO.updateProcedureRun({id: procedureRunId, description}, {supportsAsync: false});
          refetch();
        }}
        isDisabled={false} // TODO (BNCH-159710): Check procedure run permissions
        emptyValueAllowed={true}
        value={runDescription}
        maxLength={MAX_PROCEDURE_RUN_DESCRIPTION_LENGTH}
        type={TextFieldType.TEXTAREA}
        autoSize={true}
        textPlaceholder={`Add ${runLabel} description`}
        // Blocks the Design tab's Edit action while the description is being edited.
        onIsBeingEditedChanged={setIsProcedureRunDescriptionInEditMode}
      />
    </div>
  );

  const withActionIconColor = (item: ActionItem): ActionItem => ({...item, className: classes.actionIcon});
  let fullTextButtons: Array<FullTextButton>;
  let actionIconButtons: Array<ActionItem>;
  if (isDesignTab) {
    fullTextButtons = runDesignActions != null ? getProcedureRunHeaderButtons(runDesignActions) : [];
    // The first two actions render as icons, and the rest go to overflow.
    actionIconButtons = [
      linkToExistingUnitOperationsAction,
      copyLinkAction,
      analyzeAction,
      exportAuditAction,
    ].map((item) => withActionIconColor(item));
  } else {
    fullTextButtons = [
      actionToFullTextButton(analyzeAction, 'primary'),
      actionToFullTextButton(linkToExistingUnitOperationsAction, 'secondary'),
    ];
    actionIconButtons = [copyLinkAction, exportAuditAction].map((item) => withActionIconColor(item));
  }

  const studyArchiveItem = {id: study.id, name: study.name, archiveRecord: study.archiveRecord};
  const badges: Array<BadgeInfo> | undefined = isArchived(studyArchiveItem)
    ? [{text: getArchiveText(studyArchiveItem), color: 'gray'}]
    : undefined;

  return (
    <Header
      title={procedureRunName}
      dataTestAttrs={{component: 'ProcedureRunHeader', element: 'root'}}
      crumbs={[
        {
          label: study.name,
          dataTestAttrs: {component: 'ProcedureRunHeader', element: 'studyBreadcrumb'},
          onClick: () => {
            onStudyCrumbClick();
          },
        },
      ]}
      icon={getProcedureRunIcon(getProcedureTypeForStudyType(studyType))}
      classNames={{body: classes.body}}
      badges={badges}
      fullTextButtons={fullTextButtons}
      actionIconButtons={actionIconButtons}
      miscItems={[<CreatedAt key="createdAt" createdAt={procedureRunCreatedAt} />]}
      isCollapsed={shouldCollapseHeader}
    >
      {descriptionEditor}
    </Header>
  );
}

const useStyles = createStyles((theme) => ({
  body: {boxShadow: 'none'},
  actionIcon: {
    color: theme.colors.gray[6],
  },
  descriptionEditor: {
    width: '100%',
  },
}));
